<template>
  <SfDrawer :open="isOpen" :title="title" @close="close">
    <div
      v-if="loading"
      class="tw-flex tw-flex-col tw-gap-4"
      data-testid="group-panel-loading"
    >
      <SfSkeletonLoader
        v-for="placeholder in GROUP_PANEL_SKELETON_ROWS"
        :key="placeholder"
        width="100%"
        height="4rem"
      />
    </div>

    <div
      v-else-if="loadError"
      class="tw-text-sm tw-text-sf-slate-dark-1"
      role="alert"
      data-testid="group-panel-load-error"
    >
      {{ I18n.t('group.panel.load_error') }}
    </div>

    <div v-else class="tw-flex tw-flex-col tw-gap-4">
      <PanelCard :title="I18n.t('group.panel.details_title')">
        <div class="tw-w-80">
          <SfInput
            :id="`${idPrefix}-name`"
            v-model="name"
            :label="I18n.t('common.name')"
            :placeholder="I18n.t('group.panel.name_placeholder')"
            :has-error="nameErrors.length > 0"
            :error-text="nameErrors"
            data-testid="group-panel-name"
          />
        </div>
      </PanelCard>

      <PanelCard
        :title="I18n.t('group.panel.staff_count', { count: members.count })"
      >
        <AccessList
          testid="group-panel-members"
          icon="users"
          :icon-size="GROUP_MEMBER_AVATAR_SIZE"
          :label="I18n.t('group.panel.staff_hint')"
          :empty-label="I18n.t('group.panel.no_members_added')"
          :find-placeholder="I18n.t('group.panel.search_assigned_members')"
          :items="members.items"
          :paging="members.paging"
          :adding="{
            label: I18n.t('group.panel.add_members'),
            placeholder: I18n.t('group.panel.search_members'),
            search: members.searchToAdd,
          }"
          @add="addMember"
          @remove="removeMember"
          @search="members.search"
          @load-more="members.loadMore"
        >
          <template #icon="{ item }">
            <SfUserAvatar :name="item.title" size="md" />
          </template>
        </AccessList>
      </PanelCard>

      <PanelCard
        :title="I18n.t('group.panel.files_count', { count: files.count })"
      >
        <AccessList
          testid="group-panel-files"
          icon="building"
          :label="I18n.t('group.panel.files_hint')"
          :empty-label="I18n.t('group.panel.no_files_added')"
          :find-placeholder="I18n.t('group.panel.search_assigned_files')"
          :items="files.items"
          :paging="files.paging"
          :adding="{
            label: I18n.t('group.panel.add_client_file'),
            placeholder: I18n.t('group.panel.search_client_files'),
            search: files.searchToAdd,
          }"
          @add="addFile"
          @remove="removeFile"
          @search="files.search"
          @load-more="files.loadMore"
        />
      </PanelCard>
    </div>

    <template #footer>
      <p
        v-if="saveError"
        class="tw-m-0 tw-mr-auto tw-text-sm tw-text-sf-danger"
        role="alert"
        data-testid="group-panel-save-error"
      >
        {{ saveError }}
      </p>

      <SfButtonDs
        v-if="!creating"
        variant="danger"
        :disabled="loading || loadError || saving || deleting"
        data-testid="group-panel-delete"
        @click="confirmingDelete = true"
      >
        {{ I18n.t('group.panel.delete') }}
      </SfButtonDs>

      <SfButtonDs
        variant="primary"
        :disabled="loading || loadError || saving || deleting"
        :loading="saving"
        data-testid="group-panel-save"
        @click="onSave"
      >
        {{ I18n.t('group.panel.save_changes') }}
      </SfButtonDs>
    </template>
  </SfDrawer>

  <SfModal
    v-if="confirmingDelete"
    size="s"
    data-testid="group-panel-delete-modal"
    @close="confirmingDelete = false"
  >
    <template #title>
      {{ I18n.t('group.panel.delete_title') }}
    </template>
    <template #body>
      <p class="tw-m-0 tw-text-center tw-text-sm tw-text-sf-slate-dark-1">
        {{ I18n.t('group.panel.delete_confirm', { name }) }}
      </p>
    </template>
    <template #footer>
      <div class="tw-flex tw-justify-center tw-gap-2">
        <SfButtonDs variant="secondary" @click="confirmingDelete = false">
          {{ I18n.t('common.cancel') }}
        </SfButtonDs>
        <SfButtonDs
          variant="danger"
          :loading="deleting"
          data-testid="group-panel-delete-confirm"
          @click="onRemove"
        >
          {{ I18n.t('group.panel.delete') }}
        </SfButtonDs>
      </div>
    </template>
  </SfModal>
</template>

<script setup lang="ts">
import { computed, useId, watch } from 'vue'
import { storeToRefs } from 'pinia'

import {
  GROUP_MEMBER_AVATAR_SIZE,
  GROUP_PANEL_SKELETON_ROWS,
  QUERY_KEYS,
} from '@app/application/user-management/constants'
import { toRecordId } from '@app/application/user-management/utils/list-query'
import { useGroupPanelStore } from '@app/application/user-management/stores/group-panel'
import useGroupItemList from '@app/application/user-management/composables/use-group-item-list'
import useI18n from '@app/shared/vue/composables/use-I18n'
import useUrlStorage from '@app/shared/vue/composables/use-url-storage'
import AccessList from '@app/application/user-management/components/user-panel/access-list.vue'
import PanelCard from '@app/application/user-management/components/panel-card.vue'
import { SfButtonDs } from '@silverfin/ui-components'
import SfDrawer from '@app/shared/vue/components/sf-drawer/sf-drawer.vue'
import SfInput from '@app/shared/vue/components/sf-input/sf-input.vue'
import SfModal from '@app/shared/vue/components/sf-modal/sf-modal.vue'
import SfSkeletonLoader from '@app/shared/vue/components/sf-skeleton-loader.vue'
import SfUserAvatar from '@app/shared/vue/components/sf-user-avatar/sf-user-avatar.vue'

defineOptions({ name: 'GroupPanel' })

const emit = defineEmits<{ saved: [] }>()

const I18n = useI18n()
const idPrefix = useId()
const store = useGroupPanelStore()

const {
  confirmingDelete,
  creating,
  deleting,
  isOpen,
  loadError,
  loading,
  name,
  nameErrors,
  openGroupId,
  saveError,
  saving,
} = storeToRefs(store)
const { addFile, addMember, close, removeFile, removeMember } = store

const members = useGroupItemList('members', (rows) =>
  rows.map((member) => ({
    key: member.id,
    title: member.name,
    subtitle: member.email,
    removable: true,
  }))
)

const files = useGroupItemList('files', (rows) =>
  rows.map((file) => ({ key: file.id, title: file.name, removable: true }))
)

// A new group has no id yet, so only an existing one goes in the URL.
const linkedGroupId = useUrlStorage<number | null>(
  QUERY_KEYS.GROUP,
  null,
  (id) => (id === null ? '' : String(id)),
  { deserialize: toRecordId }
)

const title = computed(() =>
  creating.value ? I18n.t('group.new_group') : name.value
)

const finish = (): void => {
  emit('saved')
  close()
}

const onSave = async (): Promise<void> => {
  if (await store.save()) finish()
}

const onRemove = async (): Promise<void> => {
  if (await store.remove()) finish()
}

watch(
  linkedGroupId,
  (id) => {
    if (id === openGroupId.value) return

    if (id === null) {
      close()
    } else {
      void store.open(id)
    }
  },
  { immediate: true }
)

watch(openGroupId, (id) => {
  linkedGroupId.value = id
})
</script>
